<template>
    <PhpEolLayout
        :supported-modules="supportedModules"
        :active-module="activeModule"
        @module-changed="changeModule"
    >
        <DRow
            align-horizontal="start"
            align-vertical="end"
            class="mb-3"
        >
            <DCol lg="2">
                <DInput
                    id="parent_id_search"
                    v-model="filterData.parent_id"
                    :label="__('tools.php_eol.first_email_candidates.filters.parent_contract_id')"
                    @keydown.enter.exact.prevent="loadPage(records.current_page, filterData)"
                />
            </DCol>
            <DCol lg="3">
                <DInput
                    id="domain_search"
                    v-model="filterData.domain"
                    :label="__('tools.php_eol.first_email_candidates.filters.domain')"
                    @keydown.enter.exact.prevent="loadPage(records.current_page, filterData)"
                />
            </DCol>
            <DCol lg="2">
                <DInput
                    id="php_version_search"
                    v-model="filterData.php_version"
                    :label="__('tools.php_eol.first_email_candidates.filters.php_version')"
                    @keydown.enter.exact.prevent="loadPage(records.current_page, filterData)"
                />
            </DCol>
            <DCol lg="2">
                <DInput
                    id="batch_id_search"
                    v-model="filterData.batch_id"
                    type="number"
                    :label="__('tools.php_eol.first_email_candidates.filters.batch_id')"
                    @keydown.enter.exact.prevent="loadPage(records.current_page, filterData)"
                />
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="loadPage(records.current_page, filterData)"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="loadPage(records.current_page, {})"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCard padding>
                <DTable
                    :items="isProcessed ? props.records.data : items"
                    :fields="fields"
                    columnSorter
                    :tableProps="{
                        hover: true,
                    }"
                >
                    <template #parent_contract_id="{item}">
                        <td v-if="userPermissions.includes('contract.view') && item.parent_contract_id !== null">
                            <Link :href="generateContractRoute(item.parent_contract.customer_id, item.parent_contract_id)">
                                {{ item.parent_contract_id }}
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.parent_contract_id }}
                        </td>
                    </template>
                    <template #domain="{item}">
                        <td>
                            {{ item.domain }}
                        </td>
                    </template>
                    <template #php_version="{item}">
                        <td>
                            {{ item.php_version }}
                        </td>
                    </template>
                    <template #status_label="{item}">
                        <td>
                            {{ item.status_label }}
                        </td>
                    </template>
                    <template #batch_id="{item}">
                        <td>
                            {{ item.batch_id }}
                        </td>
                    </template>
                    <template #last_error="{item}">
                        <td>
                            {{ item.last_error }}
                        </td>
                    </template>
                    <template #addon_contract_id="{item}">
                        <td v-if="userPermissions.includes('contract.view') && item.addon_contract_id !== null">
                            <Link :href="generateContractRoute(item.parent_contract.customer_id, item.addon_contract_id)">
                                {{ item.addon_contract_id }}
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.addon_contract_id }}
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="records.last_page >= 1"
                    :active-page="records.current_page"
                    :pages="records.last_page"
                    align="center"
                    @update:activePage="(page) => loadPage (page, filterData)"
                />
            </DCard>
        </DRow>
        <WebSocketLoadingModal
            :title="__('tools.php_eol.addon_candidates.broadcast.creation.notice')"
            :show-modal="showWebsocketModal"
            :internal-support-link="internalSupportLink"
            :soft-timeout="400"
            @close="showWebsocketModal = false"
        />
        <ProcessRecordsBatchModal
            is-confirm
            is-awaiting-validation
            is-medium
            :show-modal="showModal"
            :active-module="activeModule"
            :action="actionRoute"
            :batch-id="batchId"
            :batches="batches"
            :is-skipping-process-allowed="isSkippingProcessAllowed"
            @close-modal="showModal = false"
            @form-submitted="showWebsocketModal = true"
        />
    </PhpEolLayout>
    <DFabs>
        <DFab
            v-if="userPermissions.includes('contract.fulfillment') && isProcessed === false"
            id="btn-add"
            icon="plus"
            :disabled="props.records.data.length === 0"
            :label="__('tools.php_eol.batch_process_actions.process_button')"
            @click="showModal = true"
        />
    </DFabs>
</template>

<script setup>

import { router, usePage } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, ref } from 'vue'
import ProcessRecordsBatchModal from '~@/Pages/Contracts/PhpEol/ProcessRecordsBatchModal'
import PhpEolLayout from '~@/Pages/Contracts/PhpEol/Layout.vue'
import WebSocketLoadingModal from '~@/Components/Common/Modals/WebSocketLoadingModal'
import { route } from '~@/Composables/route'

const props = defineProps({
    records: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    actionRoute: {
        type: String,
        default: () => {
            return null
        }
    },
    indexRoute: {
        type: String,
        default: () => {
            return null
        }
    },
    batches: {
        type: Array,
        default: () => { return [] }
    },
    batchId: {
        type: Number,
        default: () => {
            return null
        }
    },
    isProcessed: {
        type: Boolean,
        default: () => {
            return false
        }
    },
    isSkippingProcessAllowed: {
        type: Boolean,
        default: () => {
            return false
        }
    },
    supportedModules: {
        type: Array,
        default () {
            return []
        }
    },
    activeModule: {
        type: String,
        default () {
            return ''
        }
    }
})

const fields = computed(() => {
    const columns = [
        { key: 'parent_contract_id', label: __('tools.php_eol.first_email_candidates.table.parent_contract_id') },
        { key: 'domain', label: __('tools.php_eol.first_email_candidates.table.domain') },
        { key: 'php_version', label: __('tools.php_eol.first_email_candidates.table.php_version') },
        { key: 'status_label', label: __('tools.php_eol.first_email_candidates.table.status') },
        { key: 'batch_id', label: __('tools.php_eol.first_email_candidates.table.batch') },
    ]

    if (props.isProcessed === false) {
        return [
            ...columns,
            { key: 'last_error', label: __('tools.php_eol.first_email_candidates.table.error_message') },
        ]
    }

    return [
        ...columns,
        { key: 'addon_contract_id', label: __('tools.php_eol.first_email_candidates.table.addon_contract_id') },
    ]
})

const items = computed(() => {
    return props.records.data.map(function (item) {
        return {
            ...item,
            _classes: getRowClass(item)
        }
    })
})

const getRowClass = function (item) {
    return (item?.last_error ?? '').length > 0 ? 'table-danger' : ''
}

const userPermissions = computed(() => usePage().props.auth.permissions)
const internalSupportLink = computed(() => usePage().props.tenantSettings.internal_support_url)
const showModal = ref(false)
const showWebsocketModal = ref(false)
const filterData = ref(props.filters)

const generateContractRoute = function (customerId, contractId) {
    return route('customers.customers.contracts.show', { customer: customerId, contract: contractId })
}

const loadPage = function (page, queryParameters = {}) {
    if (page > 1) {
        queryParameters.page = page
    }

    queryParameters.module = props.activeModule
    router.get(route(props.indexRoute), queryParameters)
}

const changeModule = function (moduleName) {
    router.get(route('contracts.php-eol-bulk.index'), { module: moduleName })
}

const processContractsDone = function (message) {
    showWebsocketModal.value = false
    usePage().props.flash.message = message
    setTimeout(() => { loadPage(1) }, 2000)
}

const processContractsFailed = function (error) {
    showWebsocketModal.value = false

    usePage().props.flash.message = null
    usePage().props.flash.errorMessages = error
}

const showMessage = function (message) {
    usePage().props.flash.message = message
}

window.laravelEcho
    .channel('onecrm.phpeol.batch')
    .listen(
        '.phpeol.contracts_processed',
        ({ message }) => {
            processContractsDone(message)
        }
    ).listen(
        '.phpeol.contracts_processing_failed',
        ({ error }) => {
            processContractsFailed(error)
        }
    ).listen(
        '.phpeol.json_successfully_downloaded',
        ({ message }) => {
            showMessage(message)
        }
    )
    .listen(
        '.phpeol.json_download_failed',
        ({ error }) => {
            processContractsFailed(error)
        }
    )
    .listen(
        '.phpeol.json_download_estimated_time',
        ({ message }) => {
            showMessage(message)
        }
    )

</script>

<style lang="scss" scoped>
.header-actions {
    display: grid;
    justify-items: flex-start;
    gap: 10px;
}

.table-text-color-red {
    color: red !important;
}
</style>
